import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useIdentity } from '../features/identity/state/useIdentity';
import { LoginPage } from '../features/identity/pages/LoginPage';
import { isSafeReturnTo } from '../shared/isSafeReturnTo';
import { CompanyPickerPage } from '../features/identity/pages/CompanyPickerPage';
import { ForgotPasswordPage } from '../features/identity/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '../features/identity/pages/ResetPasswordPage';
import { UnlockRequestPage } from '../features/identity/pages/UnlockRequestPage';
import { UnlockCompletePage } from '../features/identity/pages/UnlockCompletePage';
import { AccessDeniedPage } from '../features/identity/pages/AccessDeniedPage';
import { AdminSignUpPage } from '../features/admin-signup/pages/AdminSignUpPage';
import { PartnerSignUpPage } from '../features/admin-signup/pages/PartnerSignUpPage';
import { BusinessProfileSetupPage } from '../features/business-profile-setup/pages/BusinessProfileSetupPage';
import { AcceptInvitationPage } from '../features/accept-invitation/pages/AcceptInvitationPage';
import { PartnerLoginPage } from '../features/partner-signin/pages/PartnerLoginPage';
import { PartnerForgotPasswordPage } from '../features/partner-signin/pages/PartnerForgotPasswordPage';
import { PartnerResetPasswordPage } from '../features/partner-signin/pages/PartnerResetPasswordPage';
import { PartnerPortal } from '../features/partner-portal/PartnerPortal';
import { PartnerSignInPage } from '../features/partner-portal/PartnerSignInPage';
import { AuthorizedPartnersPage } from '../features/partner-portal/company/AuthorizedPartnersPage';
import { CompanyListPage } from '../features/companies/pages/CompanyListPage';
import { PeopleListPage } from '../features/people/pages/PeopleListPage';
import { EmployeeQuestionsPage } from '../features/people/pages/EmployeeQuestionsPage';
import { TaxSetupExceptionsPage } from '../features/people/pages/TaxSetupExceptionsPage';
import { EmployeeProfilePage } from '../features/people/pages/EmployeeProfilePage';
import { PayContractorsPage } from '../features/pay-contractors/pages/PayContractorsPage';
import { ContractorPaymentsListPage } from '../features/contractor-payments/pages/ContractorPaymentsListPage';
import { StatementPreviewPage } from '../features/contractor-payments/pages/StatementPreviewPage';
import { ContractorProfilePage } from '../features/people/pages/ContractorProfilePage';
import { RolesPage } from '../features/job-roles/pages/RolesPage';
import { RoleDetailPage } from '../features/job-roles/pages/RoleDetailPage';
import { BenefitsOverviewPage } from '../features/benefits-deductions/pages/BenefitsOverviewPage';
import { BenefitCategoryPage } from '../features/benefits-deductions/pages/BenefitCategoryPage';
import { DeductionsOverviewPage } from '../features/benefits-deductions/pages/DeductionsOverviewPage';
import { DeductionCategoryPage } from '../features/benefits-deductions/pages/DeductionCategoryPage';
import { TaxesStatutoryPage } from '../features/benefits-deductions/pages/TaxesStatutoryPage';
import { PaySchedulesPage } from '../features/pay-config/pages/PaySchedulesPage';
import { EarningsDeductionsPage } from '../features/pay-config/pages/EarningsDeductionsPage';
import { UsersPermissionsPage } from '../features/users-permissions/pages/UsersPermissionsPage';
import { EmployeeWorkSetupPage } from '../features/employee-invitation-settings/pages/EmployeeWorkSetupPage';
import { AuditHistoryPage } from '../features/audit-history/pages/AuditHistoryPage';
import { DashboardPage } from '../features/dashboard-v2/pages/DashboardPage';
import { AdminDashboardPage } from '../features/dashboard-portal/pages/AdminDashboardPage';
import { SuperAdminDashboardPage } from '../features/super-admin/pages/SuperAdminDashboardPage';
import { SettingsPage } from '../features/settings/pages/SettingsPage';
import { CustomPayRunLandingPage } from '../features/custom-pay-run/pages/CustomPayRunLandingPage';
import { CustomPayRunListPage } from '../features/custom-pay-run/pages/CustomPayRunListPage';
import { CustomPayRunWizard } from '../features/custom-pay-run/pages/CustomPayRunWizard';
import { PayEmployeesPage } from '../features/pay-employees/pages/PayEmployeesPage';
import { PayrollReviewListPage } from '../features/payroll-review/pages/PayrollReviewListPage';
import { PayrollReviewDetailPage } from '../features/payroll-review/pages/PayrollReviewDetailPage';
import { AccountingPage } from '../features/accounting/pages/AccountingPage';
import { PayrollDetailsPage } from '../features/reports/pages/PayrollDetailsPage';
import { EmployeePayslipsPage } from '../features/reports/pages/EmployeePayslipsPage';
import { EmployeePayslipPreviewPage } from '../features/reports/pages/EmployeePayslipPreviewPage';
import { ReportsDocumentsPage } from '../features/reports/pages/ReportsDocumentsPage';
import { AccountingReportsHubPage } from '../features/accounting-reports/pages/AccountingReportsHubPage';
import { GeneralLedgerReportPage } from '../features/accounting-reports/pages/GeneralLedgerReportPage';
import { PayrollJournalReportPage } from '../features/accounting-reports/pages/PayrollJournalReportPage';
import { PayrollLiabilitiesReportPage } from '../features/accounting-reports/pages/PayrollLiabilitiesReportPage';
import { FundingTransactionsReportPage } from '../features/accounting-reports/pages/FundingTransactionsReportPage';
import { TimeAttendancePage } from '../features/time-and-leave/pages/TimeAttendancePage';
import { TimeOffPage } from '../features/time-and-leave/pages/TimeOffPage';
import { HolidaysPage } from '../features/time-and-leave/pages/HolidaysPage';
import { ExpensesPage } from '../features/time-and-leave/pages/ExpensesPage';
import { PayrollTaxesPage } from '../features/payroll-taxes/pages/PayrollTaxesPage';
import { TaxPaymentDashboardPage } from '../features/tax-payments/pages/TaxPaymentDashboardPage';
import { TaxFilingsPage } from '../features/tax-filings/pages/TaxFilingsPage';
import { SuperAdminTaxFilingsDashboardPage } from '../features/tax-filings/pages/SuperAdminTaxFilingsDashboardPage';
import { PaymentsPage } from '../features/payments/pages/PaymentsPage';
import { IntegrationsPage } from '../features/integrations/pages/IntegrationsPage';
import { SubscriptionPage } from '../features/subscription/pages/SubscriptionPage';
import { SubscriptionConfirmationPage } from '../features/subscription/pages/SubscriptionConfirmationPage';
import { DemoFeaturePage } from '../features/subscription/pages/DemoFeaturePage';
import { BillingCenterPage } from '../features/subscription/pages/BillingCenterPage';
import { CheckoutCompletePage } from '../features/subscription/pages/CheckoutCompletePage';
import { InvoiceDetailPage } from '../features/subscription/billing/InvoiceDetailPage';
import { MockCheckoutPage, MockPortalPage } from '../features/subscription/billing/MockBilling';
import { mockBillingAffordancesEnabled } from '../shared/commercialActions';
import { MyProfilePage } from '../features/profile/pages/MyProfilePage';
import { ChangePasswordPage } from '../features/profile/pages/ChangePasswordPage';
import { SupportPage } from '../features/support/pages/SupportPage';
import { SecurityWorkspacePage } from '../features/security/pages/SecurityWorkspacePage';
import { ImportPayrollDataPage } from '../features/payroll-history-import/pages/ImportPayrollDataPage';
import { ImportWizardPage } from '../features/payroll-history-import/pages/ImportWizardPage';
import { HistoricalRunDetailPage, HistoricalRunsPage } from '../features/payroll-history-import/pages/HistoryPages';
import { EmployeeAccountCreationPage } from '../features/employee-onboarding/pages/EmployeeAccountCreationPage';
import { EmployeeSetupPage } from '../features/employee-onboarding/pages/EmployeeSetupPage';
import { EmployeeDocumentsPlaceholderPage } from '../features/employee-onboarding/pages/EmployeeDocumentsPlaceholderPage';
import { EmployeeTaxesPage } from '../features/employee-onboarding/pages/EmployeeTaxesPage';
import { isEmployeeSession } from '../features/employee-onboarding/isEmployeeSession';
import { AppShell } from './AppShell';
import { ComingLaterPage } from './ComingLaterPage';
import { TaxPaymentSettingsPage } from '../features/tax-payment-settings/pages/TaxPaymentSettingsPage';
import { CompensationRatesPage } from '../features/healthcare-rates/pages/CompensationRatesPage';

// Admin Sign Up's two, deliberately narrow public routes (Admin Sign Up plan, Decision 2 + Decision 4's
// invitation-acceptance UX), plus EPN-34 Login Security's own anonymous-reachable screens (forgot/reset
// password, account unlock, and the presentation-only Access Denied screen — see AccessDeniedPage's own
// doc comment on why it has no automatic trigger yet). Every other route in this app requires an
// authenticated session — see the gate below, which checks this list BEFORE the `if (!me)` early return
// so these paths alone can be reached while signed out. Do not add to this list without the same
// architecture review Admin Sign Up itself received: it is the one place a route can bypass the normal
// authenticated-session requirement.
const PUBLIC_ROUTE_PREFIXES = [
  '/signup',
  '/accept-invitation',
  // EPN-216: the invited EMPLOYEE's account-creation page. Anonymous by design (the invitation token is the credential);
  // every other /employee/* route below requires an authenticated Employee-role session.
  '/employee/accept-invitation',
  '/forgot-password',
  '/reset-password',
  '/unlock-request',
  '/unlock-complete',
  '/access-denied',
  // EPN-36 — Partner Sign In's own flat public routes (not nested under "/login", which isn't itself a
  // PUBLIC_ROUTE_PREFIXES entry — the regular Sign In page is reached via the `!me` gate further down,
  // not this list; Partner Sign In needs its own entry here precisely because it has no such gate).
  '/partner-login',
  '/partner-forgot-password',
  '/partner-reset-password',
  // The Partner Portal's own sign-in. Only this exact page is public; every other /partner/* route is behind the sign-in gate below.
  '/partner/sign-in',
];

export function App() {
  const { me, initializing, refresh, wantsCompanyPicker, sessionExpired, isAutoSelectingCompany } = useIdentity();
  const location = useLocation();

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Checked before the initializing/session gate below so these public routes render immediately — they
  // must never wait on the one-time "is there already a session?" check.
  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`),
  );

  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/signup" element={<AdminSignUpPage />} />
        <Route path="/signup/partner" element={<PartnerSignUpPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/employee/accept-invitation" element={<EmployeeAccountCreationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/unlock-request" element={<UnlockRequestPage />} />
        <Route path="/unlock-complete" element={<UnlockCompletePage />} />
        <Route path="/access-denied" element={<AccessDeniedPage />} />
        <Route path="/partner-login" element={<PartnerLoginPage />} />
        <Route path="/partner-forgot-password" element={<PartnerForgotPasswordPage />} />
        <Route path="/partner-reset-password" element={<PartnerResetPasswordPage />} />
        <Route path="/partner/sign-in" element={<PartnerSignInPage />} />
      </Routes>
    );
  }

  // Only the app's one-time "is there already a session?" check on load shows this — never a later
  // login()/switchCompany() call. Keying this off `loading` instead used to unmount (and silently reset)
  // LoginPage on every submission, successful or not, which meant an invalid-credential error could never
  // actually be shown. See IdentityContext.ts's own doc comment on `initializing`.
  if (initializing) {
    return (
      <div className="page page--centered">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  // Every route but /login (and the public routes above) requires an authenticated session — an
  // unauthenticated visitor never reaches AppShell or any module screen, regardless of which URL they
  // typed. Capture that originally-requested URL as a `returnTo` query param on a real navigation to
  // /login (rather than just swapping in <LoginPage/> in place, which left the address bar showing the
  // protected URL) so a successful sign-in can return the visitor to where they meant to go — LoginPage
  // itself validates and consumes `returnTo` (see its own `isSafeReturnTo`) once login completes. Never
  // done from /login itself, to avoid a `returnTo=/login` loop.
  if (!me) {
    // A visitor who typed a Partner Portal address is sent to the Partner Portal's sign-in, and returns to that address afterwards.
    if (location.pathname.startsWith('/partner/')) {
      return <Navigate to={`/partner/sign-in?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`} replace />;
    }
    if (location.pathname !== '/login') {
      const requestedPath = `${location.pathname}${location.search}`;
      return <Navigate to={`/login?returnTo=${encodeURIComponent(requestedPath)}`} replace />;
    }
    return <LoginPage sessionExpired={sessionExpired} />;
  }

  // A brief, one-time window while refresh() auto-selects a user's sole active membership (EPN-47
  // product decision A) — checked BEFORE the CompanyPickerPage gate below so a single-company user never
  // sees that page flash on the way in. See IdentityContext.ts's own doc comment on this flag.
  if (isAutoSelectingCompany) {
    return (
      <div className="page page--centered">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  // EPN-216: an Employee-role session gets the onboarding card layout ONLY — never the admin shell/sidebar or any admin
  // route. Checked after the sign-in gate (so an unauthenticated visitor to /employee/* is sent to sign-in with a returnTo)
  // and before the company picker (a pure employee account has exactly one company). This only chooses what to render;
  // the server authorizes every employee request from the token's Employee role and employee id claim.
  if (isEmployeeSession(me)) {
    return (
      <Routes>
        <Route path="/employee/setup" element={<Navigate to="/employee/setup/personal" replace />} />
        <Route path="/employee/setup/:section" element={<EmployeeSetupPage />} />
        <Route path="/employee/taxes/:section" element={<EmployeeTaxesPage />} />
        <Route path="/employee/taxes" element={<Navigate to="/employee/taxes/federal" replace />} />
        <Route path="/employee/documents" element={<EmployeeDocumentsPlaceholderPage />} />
        <Route path="*" element={<Navigate to="/employee/setup/personal" replace />} />
      </Routes>
    );
  }

  // The Partner Portal is its own workspace: a partner user has no company, so it must come BEFORE the company picker, and the portal itself asks the server whether this person belongs to a
  // partner firm. Which screen shows is a routing choice; the server authorizes every partner request.
  // A session working inside a client company (a delegated token) also lives here: the company shell is built for company users, and a delegated session has no company role behind it.
  if (location.pathname === '/partner' || location.pathname.startsWith('/partner/') || me.partnerDelegation) {
    return <PartnerPortal />;
  }

  // An `activeCompanyId` can go stale mid-session: the backend intentionally keeps honoring an
  // already-issued access token for the remainder of its own short lifetime even after the membership it
  // was issued for is revoked (see MidSessionMembershipRevocationTests.cs) — but `GET /api/me`'s own
  // `memberships` list is always read live, so a revoked membership drops out of it immediately. Without
  // this check, a stale non-null `activeCompanyId` would let the full tenant-scoped app render against a
  // company the user can no longer resolve to a current membership — BusinessViewSwitcher's own
  // `me.memberships.find(...)` lookup would silently come back empty while everything else kept rendering
  // as if nothing were wrong. Recovery is the existing CompanyPickerPage flow (it already renders the
  // user's current `me.memberships`, correctly excluding the stale company and correctly handling zero
  // remaining memberships) and an explicit re-switch through the same secure switchCompany() path — never
  // an automatic move into a different company on the user's behalf.
  const hasValidActiveCompany =
    !!me.activeCompanyId && me.memberships.some((membership) => membership.companyId === me.activeCompanyId);

  if (!hasValidActiveCompany || wantsCompanyPicker) {
    return <CompanyPickerPage />;
  }

  // EPN-47: a single-company user's auto-selection (above) can settle a moment before LoginPage's own
  // returnTo navigation (navigateAfterCompletedLogin) has applied — both are legitimate, independently
  // triggered navigations, and whichever one React Router's async data router happens to commit doesn't
  // reliably match issue order. Computing the SAME safe returnTo target here means it no longer matters
  // which one wins: an authenticated, company-established visitor who is (even transiently) still at
  // "/login" always lands in the right place, not a hardcoded default that would silently discard a real
  // deep link. Recomputed on every render — inexpensive, and never stale. EPN-91: the no-returnTo fallback
  // is the Admin Portal default landing route, not the old standalone /dashboard.
  const loginCatchAllTarget = (() => {
    const returnTo = new URLSearchParams(location.search).get('returnTo');
    // EPN-91: the Admin Portal is the default authenticated landing destination — /dashboard (the
    // standalone, shell-less legacy/reference route) is no longer it. See "/" and the "*" catch-all below.
    return isSafeReturnTo(returnTo) ? returnTo : '/dashboards/admin-dashboard';
  })();

  // EPN-216: a signed-in non-Employee (for example an admin) never uses the employee onboarding routes; they go to their
  // normal landing page (the same default the authenticated catch-all uses).
  if (location.pathname.startsWith('/employee/')) {
    return <Navigate to="/dashboards/admin-dashboard" replace />;
  }

  // UX QA-001: the active business is the authoritative scope of everything rendered below. Keying the whole authenticated route tree on it
  // remounts the shell and the current page when the Business View changes, so every business-scoped fetch (dashboard totals, the
  // subscription banner and badge, notifications, the open-question count) runs again against the new company, in-flight requests of the
  // previous company are abandoned with their components, and no per-company UI state (filters, drafts, open dialogs) carries over. The
  // URL is untouched, so the user stays on the same screen and no full-page reload is needed.
  return (
    <Routes key={me.activeCompanyId}>
      {/* Standalone, shell-less route — pixel-matches docs/design-reference/escochex-official-dashboard.png,
          which has no EPN topbar or sidebar. Every other route below keeps the normal AppShell untouched.
          See docs/dashboard-architecture.md for why this route alone opts out of the shared shell. EPN-91:
          no longer the app's default landing route (see "/" below) — kept reachable for legacy/reference/
          testing purposes only. */}
      <Route path="/dashboard" element={<DashboardPage />} />

      <Route element={<AppShell />}>
        {/* EPN-91: the Admin Portal (EPN-42/45/46) is the normal authenticated EPN experience — root,
            authenticated /login, and the authenticated catch-all below all default here now. */}
        <Route path="/" element={<Navigate to="/dashboards/admin-dashboard" replace />} />
        <Route path="/companies" element={<CompanyListPage />} />
        {/* EPN-45: /people redirects to the default employees tab — kept as a real route (not removed)
            since TopHeader.tsx's own "Go to full app" link still targets it directly. */}
        <Route path="/people" element={<Navigate to="/people/employees" replace />} />
        {/* EPN-216: the Add Employee dialog on the People list is the only add flow; the old page redirects there. */}
        <Route path="/people/new" element={<Navigate to="/people/employees" replace />} />
        {/* Job Role Library (PR 1): static `roles` segments outrank the dynamic /people/:section and /people/:employeeId/:step
            routes below, exactly like /people/contractors/:contractorId. Every permission decision is the server's. */}
        <Route path="/people/roles" element={<RolesPage />} />
        <Route path="/people/roles/:roleId" element={<RoleDetailPage />} />
        {/* People > Benefits and Deductions, Phase 1 (docs/benefits-deductions-contract.md section 1). These static paths must be explicit: the dynamic
            /people/:section and /people/:employeeId/:step routes below would otherwise capture them (a static segment outranks a dynamic one, but only if the
            static route exists). The seven category leaves of each parent share one page component, keyed by category so no state leaks between them.
            Every permission decision is the server's: a 403 renders each page's own no-access or read-only state. */}
        <Route path="/people/benefits" element={<BenefitsOverviewPage />} />
        <Route path="/people/benefits/health-wellness" element={<BenefitCategoryPage category="health-wellness" />} />
        <Route path="/people/benefits/insurance" element={<BenefitCategoryPage category="insurance" />} />
        <Route path="/people/benefits/retirement-savings" element={<BenefitCategoryPage category="retirement-savings" />} />
        <Route path="/people/benefits/financial-equity" element={<BenefitCategoryPage category="financial-equity" />} />
        <Route path="/people/benefits/family-lifestyle" element={<BenefitCategoryPage category="family-lifestyle" />} />
        <Route path="/people/benefits/leave-wellbeing" element={<BenefitCategoryPage category="leave-wellbeing" />} />
        <Route path="/people/benefits/other-custom" element={<BenefitCategoryPage category="other-custom" />} />
        <Route path="/people/deductions" element={<DeductionsOverviewPage />} />
        <Route path="/people/deductions/benefit-premiums" element={<DeductionCategoryPage category="benefit-premiums" />} />
        <Route path="/people/deductions/retirement-contributions" element={<DeductionCategoryPage category="retirement-contributions" />} />
        <Route path="/people/deductions/garnishments-levies" element={<DeductionCategoryPage category="garnishments-levies" />} />
        <Route path="/people/deductions/loans-repayments" element={<DeductionCategoryPage category="loans-repayments" />} />
        <Route path="/people/deductions/union-charitable-other" element={<DeductionCategoryPage category="union-charitable-other" />} />
        <Route path="/people/deductions/taxes-statutory" element={<TaxesStatutoryPage />} />
        <Route path="/people/deductions/other-custom" element={<DeductionCategoryPage category="other-custom" />} />
        {/* EPN-38: a two-segment path, structurally distinct from the single-segment /people/:section
            below it — React Router's route ranking always prefers a static segment (this one, and
            /people/new above) over a dynamic :section param at the same position, regardless of
            declaration order, so this never collides with the section-tab route. */}
        {/* EPN-216: static two-segment path, outranks /people/:section. */}
        <Route path="/people/employee-questions" element={<EmployeeQuestionsPage />} />
        {/* Tax setup exceptions: a static path, so the dynamic /people/:section route does not capture it. Read-only; the server decides who may read it (employees.read). */}
        <Route path="/people/tax-setup-exceptions" element={<TaxSetupExceptionsPage />} />
        <Route path="/people/:employeeId/:step" element={<EmployeeProfilePage />} />
        {/* EPN-74: a real contractor profile/edit page — static "contractors" segment outranks the
            dynamic :employeeId above for a URL shaped this way, so no route ambiguity. */}
        <Route path="/people/contractors/:contractorId" element={<ContractorProfilePage />} />
        <Route path="/people/:section" element={<PeopleListPage />} />
        <Route path="/pay-schedules" element={<PaySchedulesPage />} />
        <Route path="/earnings-deductions" element={<EarningsDeductionsPage />} />
        <Route path="/payroll-processing" element={<CustomPayRunLandingPage />} />
        <Route path="/payroll-processing/history" element={<CustomPayRunListPage />} />
        <Route path="/payroll-processing/:runId/:step" element={<CustomPayRunWizard />} />
        {/* EPN-104: Payroll Review (Module 7) — the approve/reject screen for payroll runs handed off from Custom
            Pay Run. Both routes sit inside the normal authenticated shell; every permission decision is the
            server's (a 403 renders the screen's own forbidden/read-only states). */}
        <Route path="/payroll-review" element={<PayrollReviewListPage />} />
        <Route path="/payroll-review/:runId" element={<PayrollReviewDetailPage />} />
        <Route path="/accounting" element={<AccountingPage />} />
        <Route path="/reports" element={<ReportsDocumentsPage />} />
        <Route path="/time-attendance" element={<TimeAttendancePage />} />
        <Route path="/time-off" element={<TimeOffPage />} />
        <Route path="/tax-payments" element={<TaxPaymentDashboardPage />} />
        <Route path="/payroll-taxes" element={<PayrollTaxesPage />} />
        <Route path="/tax-filings" element={<TaxFilingsPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/settings/subscription" element={<SubscriptionPage />} />
        <Route path="/settings/subscription/confirmation" element={<SubscriptionConfirmationPage />} />
        {/* EPN-105 — the billing center is reachable in every subscription state. The checkout-return page never
            trusts its query string; it asks the server. The mock checkout/portal pages exist only in a mock-backed,
            non-production build. */}
        <Route path="/settings/billing" element={<BillingCenterPage />} />
        <Route path="/settings/billing/invoices/:invoiceId" element={<InvoiceDetailPage />} />
        <Route path="/settings/billing/checkout-complete" element={<CheckoutCompletePage />} />
        {mockBillingAffordancesEnabled && <Route path="/settings/billing/mock-checkout" element={<MockCheckoutPage />} />}
        {mockBillingAffordancesEnabled && <Route path="/settings/billing/mock-portal" element={<MockPortalPage />} />}
        <Route path="/subscription-demo/:featureKey" element={<DemoFeaturePage />} />
        <Route path="/users-permissions" element={<UsersPermissionsPage />} />
        <Route path="/audit-history" element={<AuditHistoryPage />} />
        <Route path="/settings" element={<SettingsPage />} />

        {/* EPN-42 — real Overview Dashboard content for the Admin portal, inside the normal AppShell,
            distinct from the standalone /dashboard above. EPN-45's Side Menu "Overview" leaf targets this
            route, and EPN-91 makes it the app's default authenticated landing destination (see "/",
            authenticated /login, and the catch-all "*" below). */}
        <Route path="/dashboards/admin-dashboard" element={<AdminDashboardPage />} />
        <Route path="/super-admin" element={<SuperAdminDashboardPage />} />
        <Route path="/super-admin/tax-filings" element={<SuperAdminTaxFilingsDashboardPage />} />

        {/* My Profile menu destinations — see docs/design-reference/epn-my-profile-menu.png and
            docs/profile-menu-architecture.md. My Profile, Support, and Change Password are real; the rest
            are correctly-routed placeholders (ComingLaterPage), never dead links. */}
        <Route path="/profile" element={<MyProfilePage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/security-workspace" element={<SecurityWorkspacePage />} />
        <Route path="/company-profile" element={<BusinessProfileSetupPage />} />
        {/* EPN-156: the old placeholder path is kept so the profile menu and any bookmark still land on the page. */}
        <Route path="/payroll-settings-overview" element={<Navigate to="/tax-payment-settings" replace />} />
        <Route path="/tax-payment-settings" element={<TaxPaymentSettingsPage />} />
        {/* Healthcare disciplines, service types and compensation rates: the company industry, the service/visit type catalog and the company rate schedule. Every permission decision is the server's. */}
        <Route path="/settings/compensation-rates" element={<CompensationRatesPage />} />
        <Route path="/futa-credit-reduction" element={<ComingLaterPage moduleName="FUTA Credit Reduction" />} />
        <Route path="/help-support" element={<ComingLaterPage moduleName="Help & Support" />} />

        {/* EPN-45 side menu — leaves with no real screen yet, each a real route rendering ComingLaterPage,
            never a dead link. See nav.ts's own doc comment for how each of these maps to the approved
            Figma/PDF design. */}
        <Route path="/alerts-and-to-dos" element={<ComingLaterPage moduleName="Alerts and To-Dos" />} />
        <Route path="/payroll/pay-employees" element={<PayEmployeesPage />} />
        <Route path="/payroll/pay-contractors" element={<PayContractorsPage />} />
        <Route path="/payroll/employee-payslips" element={<EmployeePayslipsPage />} />
        <Route path="/payroll/employee-payslips/:payrollRunId/:employeeId" element={<EmployeePayslipPreviewPage />} />
        <Route path="/payroll/contractor-payments" element={<ContractorPaymentsListPage />} />
        <Route path="/payroll/contractor-payments/:id" element={<StatementPreviewPage />} />
        {/* Payroll History Import: read-only historical payroll brought in from a legacy export. The static
            `new` and `history` segments outrank the dynamic `:batchId/:step` wizard route. */}
        <Route path="/payroll/import-payroll-data" element={<ImportPayrollDataPage />} />
        <Route path="/payroll/import-payroll-data/new" element={<ImportWizardPage />} />
        <Route path="/payroll/import-payroll-data/history" element={<HistoricalRunsPage />} />
        <Route path="/payroll/import-payroll-data/history/:runId" element={<HistoricalRunDetailPage />} />
        <Route path="/payroll/import-payroll-data/:batchId/:step" element={<ImportWizardPage />} />
        <Route path="/payroll/download-payroll-data" element={<ComingLaterPage moduleName="Download Payroll Data" />} />
        <Route path="/people/holidays" element={<HolidaysPage />} />
        <Route path="/people/expenses" element={<ExpensesPage />} />
        <Route path="/people/import-contractors" element={<ComingLaterPage moduleName="Import Contractors" />} />
        <Route path="/people/contractor-discipline" element={<ComingLaterPage moduleName="Contractor Discipline" />} />
        <Route path="/reports/employee-details" element={<ComingLaterPage moduleName="Employee Details" />} />
        <Route path="/reports/salary-report" element={<ComingLaterPage moduleName="Salary Report" />} />
        <Route path="/reports/pay-summary-per-employee" element={<ComingLaterPage moduleName="Pay Summary per Employee" />} />
        <Route path="/reports/contractor-payments" element={<ComingLaterPage moduleName="Contractor Payments" />} />
        <Route path="/reports/contractor-summary" element={<ComingLaterPage moduleName="Contractor Summary" />} />
        <Route path="/reports/payroll-summary" element={<ComingLaterPage moduleName="Payroll Summary" />} />
        <Route path="/reports/payroll-details" element={<PayrollDetailsPage />} />
        {/* EPN-77: replaces the old /reports/accounting-report ComingLater stub with the real four-report
            Accounting Reports hub. */}
        <Route path="/reports/accounting" element={<AccountingReportsHubPage />} />
        <Route path="/reports/accounting/general-ledger" element={<GeneralLedgerReportPage />} />
        <Route path="/reports/accounting/payroll-journal" element={<PayrollJournalReportPage />} />
        <Route path="/reports/accounting/payroll-liabilities" element={<PayrollLiabilitiesReportPage />} />
        <Route path="/reports/accounting/funding-transactions" element={<FundingTransactionsReportPage />} />
        <Route path="/reports/tax-payments-report" element={<ComingLaterPage moduleName="Tax Payments Report" />} />
        <Route path="/settings/security-verification" element={<ComingLaterPage moduleName="Security Verification" />} />
        {/* Company owner's control over partner firms. No sidebar entry yet (the navigation registry is being reclassified in a separate change); reached from a partner's invitation link. */}
        <Route path="/settings/authorized-partners" element={<AuthorizedPartnersPage />} />
        <Route path="/settings/employee-work-setup" element={<EmployeeWorkSetupPage />} />
        <Route path="/settings/work-locations" element={<ComingLaterPage moduleName="Work Locations" />} />

        {/* EPN-47: the authenticated "/login" catch-all applies the same safe-returnTo check LoginPage's
            own post-login navigation uses (see loginCatchAllTarget above) instead of unconditionally
            hardcoding the default — an authenticated visitor transiently still at "/login" always lands at
            their real intended deep link, not always at the EPN-91 default. The unconditional "*" catch-all
            below has no returnTo to honor, so it always falls back to that same EPN-91 default. */}
        <Route path="/login" element={<Navigate to={loginCatchAllTarget} replace />} />
        <Route path="*" element={<Navigate to="/dashboards/admin-dashboard" replace />} />
      </Route>
    </Routes>
  );
}
